/**
 * Service for handling document storage in the browser
 */

// Constants for storage keys
const DOCUMENTS_KEY = 'survey-documents';
const APP_STATE_KEY = 'survey-app-state';

/**
 * Helper function to generate a unique ID
 * Note: This is a temporary solution until we add the uuid library
 */
const generateUniqueId = () => {
  const timestamp = new Date().getTime();
  const randomNum = Math.floor(Math.random() * 10000);
  return `doc_${timestamp}_${randomNum}`;
};

const storageService = {
  /**
   * Save all documents to localStorage
   * @param {Array} documents - Array of document objects to save
   * @returns {Promise<boolean>} Success status
   */
  saveDocuments: async (documents) => {
    try {
      localStorage.setItem(DOCUMENTS_KEY, JSON.stringify(documents));
      return true;
    } catch (error) {
      console.error('Error saving documents to localStorage', error);
      return false;
    }
  },
  
  /**
   * Load all documents from localStorage
   * @returns {Promise<Array>} Array of document objects
   */
  loadDocuments: async () => {
    try {
      const documents = localStorage.getItem(DOCUMENTS_KEY);
      return documents ? JSON.parse(documents) : [];
    } catch (error) {
      console.error('Error loading documents from localStorage', error);
      return [];
    }
  },
  
  /**
   * Save a single document to localStorage
   * @param {Object} document - Document object to save
   * @returns {Promise<boolean>} Success status
   */
  saveDocument: async (document) => {
    try {
      const documents = await storageService.loadDocuments();
      const index = documents.findIndex(doc => doc.id === document.id);
      
      if (index >= 0) {
        documents[index] = document;
      } else {
        documents.push(document);
      }
      
      return await storageService.saveDocuments(documents);
    } catch (error) {
      console.error('Error saving document to localStorage', error);
      return false;
    }
  },
  
  /**
   * Get a single document from localStorage
   * @param {string} documentId - ID of document to retrieve
   * @returns {Promise<Object|null>} Document object or null if not found
   */
  getDocument: async (documentId) => {
    try {
      const documents = await storageService.loadDocuments();
      return documents.find(doc => doc.id === documentId) || null;
    } catch (error) {
      console.error('Error getting document from localStorage', error);
      return null;
    }
  },
  
  /**
   * Delete a document from localStorage
   * @param {string} documentId - ID of document to delete
   * @returns {Promise<boolean>} Success status
   */
  deleteDocument: async (documentId) => {
    try {
      const documents = await storageService.loadDocuments();
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      return await storageService.saveDocuments(updatedDocuments);
    } catch (error) {
      console.error('Error deleting document from localStorage', error);
      return false;
    }
  },
  
  /**
   * Save application state
   * @param {Object} state - Application state object
   * @returns {Promise<boolean>} Success status
   */
  saveAppState: async (state) => {
    try {
      localStorage.setItem(APP_STATE_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      console.error('Error saving app state to localStorage', error);
      return false;
    }
  },
  
  /**
   * Load application state
   * @returns {Promise<Object|null>} Application state or null if not found
   */
  loadAppState: async () => {
    try {
      const state = localStorage.getItem(APP_STATE_KEY);
      return state ? JSON.parse(state) : null;
    } catch (error) {
      console.error('Error loading app state from localStorage', error);
      return null;
    }
  },
  
  /**
   * Clear all stored data
   * @returns {Promise<boolean>} Success status
   */
  clearStorage: async () => {
    try {
      localStorage.removeItem(DOCUMENTS_KEY);
      localStorage.removeItem(APP_STATE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing localStorage', error);
      return false;
    }
  },
  
  /**
   * Create a new document
   * @param {Object} documentData - Initial document data
   * @returns {Object} Newly created document object
   */
  createDocument: (documentData = {}) => {
    const { title = 'Untitled Document', content = '' } = documentData;
    
    // Create a new document object
    return {
      id: generateUniqueId(),
      title,
      content,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      history: {
        past: [],
        future: []
      },
      isDirty: true
    };
  }
};

export default storageService;
